import { useLayoutEffect, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import _ from 'lodash';
import { priceFormatterV3 } from '../../settings/themeSettings';

function PieChart({ identifier, data, title = "Répartition", donut = true }) {
    const [selectedData, setSelectedData] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useLayoutEffect(() => {
        // Create root and chart avec style Web 3.0
        var root = am5.Root.new(identifier);

        root.setThemes([
            am5themes_Animated.new(root),
            am5themes_Dark.new(root)
        ]);

        var chart = root.container.children.push(am5percent.PieChart.new(root, {
            layout: root.verticalLayout,
            background: am5.Rectangle.new(root, {
                fill: am5.color("#0f0f23"),
                fillOpacity: 0.95
            })
        }));

        // Create series avec effets Web 3.0
        var series = chart.series.push(am5percent.PieSeries.new(root, {
            valueField: "value",
            categoryField: "category",
            startAngle: -90,
            endAngle: 270,
            innerRadius: am5.percent(donut ? 60 : 40),
            tooltip: am5.Tooltip.new(root, {
                labelText: "{category}: {value}",
                getFillFromSprite: false,
                fill: am5.color("#1a1a2e"),
                stroke: am5.color("#64ffda")
            })
        }));

        // Couleurs Web 3.0 modernes avec gradients néon
        const colors = [
            am5.color("#00d4ff"), // Cyan néon
            am5.color("#ff3d71"), // Rose néon  
            am5.color("#00ff88"), // Vert néon
            am5.color("#ffa726"), // Orange néon
            am5.color("#ab47bc"), // Violet néon
            am5.color("#0891b2"), // Cyan foncé
            am5.color("#be185d"), // Rose foncé
            am5.color("#65a30d"), // Lime
        ];

        var _data = [];

        // Si les données contiennent déjà des categories, les utiliser directement
        if (data && data.length > 0 && data[0].category !== undefined) {
            // Données formatées avec category et value
            _data = data.filter(d => d.value > 0); // Filtrer les valeurs nulles
        } else if (data && data.length > 0 && data[0].tarif !== undefined) {
            // Données billetterie (format original avec tarif)
            data.forEach(d => {
                if (d.tarif > 0) {
                    _data.push({
                        category: d.category || "Billeterie",
                        value: d.tarif
                    });
                }
            });
        } else {
            // Données billetterie (format legacy)
            const totalVente = _.sumBy(data, (d) => d.tarif);
            const totalMarge = _.sumBy(data, (d) => d.marge > 0 ? d.marge : 0);

            if (totalVente > 0) {
                _data.push({
                    category: "Billeterie vente",
                    value: totalVente
                });
            }

            if (totalMarge > 0) {
                _data.push({
                    category: "Billeterie Marge",
                    value: totalMarge
                });
            }
        }

        // Appliquer les couleurs personnalisées Web 3.0
        series.get("colors").set("colors", colors);

        // Style Web 3.0 pour les slices
        series.slices.template.setAll({
            stroke: am5.color("#0f0f23"),
            strokeWidth: 3,
            strokeOpacity: 1,
            cursorOverStyle: "pointer",
            tooltipText: "{category}: {valuePercentTotal.formatNumber('#.00')}%"
        });

        // Effets Web 3.0 pour les slices
        series.slices.template.states.create("hover", {
            scale: 1.1,
            strokeWidth: 5,
            shadowColor: am5.color("#64ffda"),
            shadowBlur: 20,
            shadowOffsetX: 0,
            shadowOffsetY: 0
        });

        // Gestionnaire de clic pour popup
        series.slices.template.on("click", function (ev) {
            const dataContext = ev.target.dataItem.dataContext;
            setSelectedData({
                category: dataContext.category,
                value: priceFormatterV3(dataContext.value),
                rawValue: dataContext.value,
                percentage: ev.target.dataItem.get("valuePercentTotal")
            });
            setShowModal(true);
        });

        // Labels Web 3.0 modernes
        series.labels.template.setAll({
            textType: "circular",
            centerX: am5.p50,
            centerY: am5.p50,
            fontSize: "12px",
            fontWeight: "500",
            fill: am5.color("#ffffff"),
            text: "{category}"
        });

        // Ticks Web 3.0
        series.ticks.template.setAll({
            stroke: am5.color("#64ffda"),
            strokeOpacity: 0.7
        });

        // Set data
        series.data.setAll(_data);

        // Legend Web 3.0 moderne
        var legend = chart.children.push(am5.Legend.new(root, {
            centerX: am5.p50,
            x: am5.p50,
            marginTop: 15,
            marginBottom: 15,
            layout: root.gridLayout,
            maxColumns: 2
        }));

        legend.labels.template.setAll({
            fontSize: "13px",
            fontWeight: "500",
            fill: am5.color("#ffffff")
        });

        legend.valueLabels.template.setAll({
            fontSize: "12px",
            fill: am5.color("#64ffda")
        });

        legend.data.setAll(series.dataItems);

        // Animation d'entrée Web 3.0
        series.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, [data, identifier, donut]);

    // Modal popup moderne Web 3.0
    const Modal = () => (
        showModal && selectedData && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(15, 15, 35, 0.9)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999
            }} onClick={() => setShowModal(false)}>
                <div style={{
                    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                    border: '2px solid #64ffda',
                    borderRadius: '20px',
                    padding: '40px',
                    boxShadow: '0 20px 60px rgba(100, 255, 218, 0.3)',
                    color: '#ffffff',
                    textAlign: 'center',
                    minWidth: '400px',
                    animation: 'modalSlideIn 0.3s ease-out'
                }} onClick={(e) => e.stopPropagation()}>
                    <h2 style={{
                        color: '#64ffda',
                        marginBottom: '20px',
                        fontSize: '1.8rem',
                        textShadow: '0 0 20px rgba(100, 255, 218, 0.5)'
                    }}>
                        {selectedData.category}
                    </h2>
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(100, 255, 218, 0.1))',
                        borderRadius: '15px',
                        padding: '25px',
                        marginBottom: '30px',
                        border: '1px solid rgba(100, 255, 218, 0.3)'
                    }}>
                        <p style={{
                            fontSize: '2.5rem',
                            color: '#00d4ff',
                            fontWeight: 'bold',
                            textShadow: '0 0 15px rgba(0, 212, 255, 0.5)',
                            marginBottom: '15px',
                            margin: 0
                        }}>
                            {selectedData.value}
                        </p>
                        <p style={{
                            fontSize: '1.4rem',
                            color: '#00ff88',
                            fontWeight: 'bold',
                            textShadow: '0 0 10px rgba(0, 255, 136, 0.5)',
                            margin: 0
                        }}>
                            {selectedData.percentage?.toFixed(1)}%
                        </p>
                    </div>
                    <button
                        onClick={() => setShowModal(false)}
                        style={{
                            background: 'linear-gradient(135deg, #64ffda, #00d4ff)',
                            border: 'none',
                            borderRadius: '25px',
                            padding: '12px 30px',
                            color: '#1a1a2e',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 5px 15px rgba(100, 255, 218, 0.4)'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'scale(1.05)';
                            e.target.style.boxShadow = '0 8px 25px rgba(100, 255, 218, 0.6)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 5px 15px rgba(100, 255, 218, 0.4)';
                        }}
                    >
                        Fermer
                    </button>
                </div>
            </div>
        )
    );

    return (
        <>
            <div id={identifier} style={{ width: "100%", height: "100%" }}></div>
            <Modal />
            <style>{`
                @keyframes modalSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-50px) scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
            `}</style>
        </>
    );
}
export default PieChart;
